import { api, type Game, type League } from './api';

/**
 * One scores feed per league, shared by every component that asks for it.
 *
 * This replaces a WebSocket per hook instance. Four components on the home page
 * each opened their own socket to /ws/scores, and the server sends both leagues
 * down every socket, so a single visitor held four connections and threw away
 * half of each stream.
 *
 * That was the app's ceiling. A held-open socket occupies a Cloud Run
 * concurrency slot for its whole life and keeps the instance's CPU allocated
 * while it does nothing: at 80 concurrent requests per instance and two
 * instances, four sockets a visitor caps the site at roughly forty people at
 * once, and idle connections were the largest consumer of CPU seconds.
 *
 * Polling costs a tenth of a second of CPU every thirty seconds instead of
 * holding a slot continuously, and thirty seconds matches how often the backend
 * refreshes scores anyway, so nothing is lost. Polling stops entirely while the
 * tab is hidden and catches up when it comes back.
 */

export const POLL_MS = 30_000;

type Listener = (games: Game[]) => void;

interface Feed {
  games: Game[];
  listeners: Set<Listener>;
  timer?: number;
  inFlight: boolean;
  fetchedAt: number;
}

const feeds = new Map<League, Feed>();

function feedFor(league: League): Feed {
  let feed = feeds.get(league);
  if (!feed) {
    feed = { games: [], listeners: new Set(), inFlight: false, fetchedAt: 0 };
    feeds.set(league, feed);
  }
  return feed;
}

async function refresh(league: League): Promise<void> {
  const feed = feedFor(league);
  if (feed.inFlight) return;           // one request per league, however many components asked
  feed.inFlight = true;
  try {
    const games = await api.games(league);
    feed.games = games;
    feed.fetchedAt = Date.now();
    feed.listeners.forEach((l) => l(games));
  } catch {
    // keep whatever was last known good; the next tick tries again
  } finally {
    feed.inFlight = false;
  }
}

function schedule(league: League): void {
  const feed = feedFor(league);
  if (feed.timer !== undefined || !feed.listeners.size) return;
  feed.timer = window.setInterval(() => {
    if (document.visibilityState === 'hidden') return;   // nobody is looking
    void refresh(league);
  }, POLL_MS);
}

function unschedule(league: League): void {
  const feed = feedFor(league);
  if (feed.timer !== undefined) {
    window.clearInterval(feed.timer);
    feed.timer = undefined;
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    // a tab that was hidden for a while is showing stale scores
    feeds.forEach((feed, league) => {
      if (feed.listeners.size && Date.now() - feed.fetchedAt > POLL_MS) void refresh(league);
    });
  });
}

/** Listen for this league's scores. Returns the unsubscribe function. */
export function subscribe(league: League, listener: Listener): () => void {
  const feed = feedFor(league);
  feed.listeners.add(listener);
  if (feed.games.length) listener(feed.games);            // serve what is already known
  if (Date.now() - feed.fetchedAt > POLL_MS) void refresh(league);
  schedule(league);
  return () => {
    feed.listeners.delete(listener);
    if (!feed.listeners.size) unschedule(league);
  };
}

/** Force a refresh now, for a retry button. */
export function reloadNow(league: League): Promise<void> {
  return refresh(league);
}

/** Test seam: forget every feed and stop every timer. */
export function _reset(): void {
  feeds.forEach((_, league) => unschedule(league));
  feeds.clear();
}
